import { useEffect, useState } from 'react';
import { API } from '@/services/api.service';

/**
 * Loads one employee's "other leaves" history (every leave type except annual
 * leave), refetching when the year or the type filter changes.
 *
 * @param {string} employeeId
 * @param {{ year: string|null, types: string[], includeHoliday?: boolean }} filters - `includeHoliday` adds annual leave, from the approved requests.
 * @returns {{ data: Object|null, loading: boolean, error: string|null, clearError: Function }}
 */
export default function useOtherLeavesHistory(
  employeeId,
  { year, types, includeHoliday = false }
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!employeeId) {
      setData(null);
      return undefined;
    }
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await API.employees.getEmployeeOtherLeavesHistory(
          employeeId,
          {
            year: year || undefined,
            types: types.length ? types : undefined,
            includeHoliday,
          }
        );
        if (active) setData(res);
      } catch (err) {
        if (active) setError(err?.response?.data?.message || err.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [employeeId, year, types, includeHoliday]);

  return { data, loading, error, clearError: () => setError(null) };
}
